import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { addDoc, collection, doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { db, storage } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { hasCreatorAccess } from "../utils/roleUtils";
import { getCreatorTiers } from "../services/supportTierService";
import {
  TIER_ACCESS_OPTIONS,
  afterCloseLabel,
  calculateCreatorReceive,
  contentTypeSummary,
  isFoundingCreator,
  tierAccessLabel,
} from "../services/dropPublishService";
import {
  createMuxDirectUpload,
  formatFileSize,
  uploadFileToMux,
  uploadMediaToStorage,
  validateMediaFile,
  waitForMuxReady,
} from "../services/muxUploadService";

import CreatorStudioLayout from "./creator/CreatorStudioLayout";

import "./PostADrop.css";

const STROKE = 1.8;
const TITLE_MAX = 80;
const DESC_MAX = 500;
const ARTICLE_MIN = 50;
const MY_DROPS_PATH = "/dashboard";

const STEP_LABELS = ["Upload", "Details", "Audience", "Pricing", "After close"];
const EXCLUSIVE_TIER_OPTIONS = TIER_ACCESS_OPTIONS.filter((o) => !o.wide);

function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function StepIndicator({ stepsDone, activeIndex }) {
  const nodes = [];
  STEP_LABELS.forEach((label, i) => {
    if (i > 0) {
      nodes.push(
        <div key={`line-${i}`} className={`step-line${stepsDone[i - 1] ? " done" : ""}`} />
      );
    }
    const done = stepsDone[i];
    const active = i === activeIndex;
    const numClass = done ? "done" : active ? "active" : "pending";

    nodes.push(
      <div key={label} className="step-item">
        <div className={`step-num ${numClass}`}>
          {done ? (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            i + 1
          )}
        </div>
        <span className={`step-label${active ? " active" : ""}`}>{label}</span>
      </div>
    );
  });
  return <div className="steps">{nodes}</div>;
}

function OptionCard({ selected, variant, onClick, icon, title, desc, hand, wide }) {
  const selectedClass =
    variant === "green" ? "selected-green" : variant === "dark" ? "selected-dark" : "selected";

  return (
    <button
      type="button"
      className={`option-card${selected ? ` ${selectedClass}` : ""}${wide ? " option-card-wide" : ""}`}
      onClick={onClick}
      style={wide ? { marginBottom: 10 } : undefined}
    >
      <div className="option-check"><CheckIcon /></div>
      {wide ? (
        <div className="option-row">
          {icon}
          <div>
            <h4 style={selected ? { color: "var(--gold)", fontSize: 14, fontWeight: 700, marginBottom: 3 } : { fontSize: 14, fontWeight: 700, marginBottom: 3 }}>
              {title}
            </h4>
            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>{desc}</p>
          </div>
        </div>
      ) : (
        <>
          {icon}
          <h4>{title}</h4>
          <p>{desc}</p>
          {hand && (
            <div style={{ marginTop: 10, fontFamily: "Caveat, cursive", fontSize: 13, color: "var(--text-muted)" }}>
              {hand}
            </div>
          )}
        </>
      )}
    </button>
  );
}

function normalizeAfterClose(value) {
  if (value === "archive" || value === "paid_archive") return "paid_archive";
  if (value === "forever" || value === "gone_forever") return "gone_forever";
  return value || "gone_forever";
}

function normalizeTierAccess(drop) {
  if (drop?.tierAccess) return drop.tierAccess;
  if (drop?.accessType === "subscription" || drop?.tierRequired) {
    return drop.tierRequired || drop.unlock_tier || "tier1";
  }
  return "everyone";
}

export default function PostADrop() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editDropId = searchParams.get("edit") || "";
  const { currentUser, userProfile, loading: authLoading } = useAuth();

  const fileInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const [creatorTiers, setCreatorTiers] = useState([]);
  const [contentMode, setContentMode] = useState("file"); // file | article
  const [mediaFile, setMediaFile] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [uploadPhase, setUploadPhase] = useState("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [muxPlaybackId, setMuxPlaybackId] = useState("");
  const [autoThumbnail, setAutoThumbnail] = useState("");
  const [articleBody, setArticleBody] = useState("");

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");

  const [tierAccess, setTierAccess] = useState("everyone");
  const [isFree, setIsFree] = useState(true);
  const [price, setPrice] = useState("9.00");
  const [afterClose, setAfterClose] = useState("gone_forever");
  const [archivePrice, setArchivePrice] = useState("9.00");

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [editLoading, setEditLoading] = useState(Boolean(editDropId));
  const [editingDropId, setEditingDropId] = useState("");
  const [editLoadError, setEditLoadError] = useState("");
  const [originalStatus, setOriginalStatus] = useState("open");

  const hasTierSetup = creatorTiers.length > 0;
  const founding = isFoundingCreator(userProfile);
  const receiveCalc = useMemo(
    () => calculateCreatorReceive(isFree ? 0 : price, userProfile),
    [isFree, price, userProfile]
  );
  const isEditMode = Boolean(editingDropId);

  const contentReady = contentMode === "article"
    ? articleBody.trim().length >= ARTICLE_MIN
    : (uploadPhase === "ready" && Boolean(mediaUrl))
      || (isEditMode && Boolean(mediaUrl || muxPlaybackId));

  useEffect(() => {
    if (!currentUser) return;
    if (Array.isArray(userProfile?.tiers) && userProfile.tiers.length > 0) {
      setCreatorTiers(userProfile.tiers);
      return;
    }
    getCreatorTiers(currentUser.uid).then(setCreatorTiers).catch(() => setCreatorTiers([]));
  }, [currentUser, userProfile?.tiers]);

  useEffect(() => {
    if (!editDropId || !currentUser) {
      setEditLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setEditLoading(true);
      setEditLoadError("");
      try {
        const snap = await getDoc(doc(db, "drops", editDropId));
        if (!snap.exists()) throw new Error("Drop not found.");
        const data = snap.data() || {};
        if (data.creatorId !== currentUser.uid) {
          throw new Error("You can only edit your own drops.");
        }
        if (cancelled) return;

        const type = data.contentType || "photo";
        const free = Boolean(data.isFree) || Number(data.price) === 0 || data.accessType === "free";
        setEditingDropId(snap.id);
        setOriginalStatus(data.status === "draft" ? "draft" : "open");
        setTitle(data.title || "");
        setSubtitle(data.subtitle || "");
        setDescription(data.description || "");
        setTierAccess(normalizeTierAccess(data));
        setIsFree(free);
        setPrice(free ? "9.00" : String(Number(data.price || 9).toFixed(2)));
        setAfterClose(normalizeAfterClose(data.afterClose));
        setArchivePrice(
          data.archivePrice != null
            ? String(Number(data.archivePrice).toFixed(2))
            : String(Number(data.price || 9).toFixed(2))
        );
        setThumbnailPreview(data.thumbnailUrl || "");
        setMuxPlaybackId(data.muxPlaybackId || data.playbackId || "");
        setAutoThumbnail(data.thumbnailUrl || "");

        if (type === "article") {
          setContentMode("article");
          setContentType("article");
          setArticleBody(data.mediaUrl || data.articleBody || data.description || "");
          setMediaUrl("");
          setUploadPhase("idle");
        } else {
          setContentMode("file");
          setContentType(type);
          setArticleBody("");
          setMediaUrl(data.mediaUrl || data.thumbnailUrl || "");
          setUploadPhase(data.mediaUrl || data.muxPlaybackId ? "ready" : "idle");
        }
      } catch (err) {
        if (!cancelled) {
          setEditingDropId("");
          setEditLoadError(err.message || "Could not load drop for editing.");
        }
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editDropId, currentUser]);

  const showToast = useCallback((text, type = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const resetFileUpload = () => {
    setMediaFile(null);
    setContentType(null);
    setUploadPhase("idle");
    setUploadProgress(0);
    setUploadError("");
    setMediaUrl("");
    setMuxPlaybackId("");
    setAutoThumbnail("");
  };

  const switchToArticle = () => {
    resetFileUpload();
    setContentMode("article");
    setContentType("article");
  };

  const switchToFile = () => {
    setContentMode("file");
    setArticleBody("");
    setContentType(null);
    setUploadPhase("idle");
  };

  const processMediaFile = useCallback(async (file) => {
    const validation = validateMediaFile(file);
    if (!validation.ok) {
      showToast(validation.error, "error");
      return;
    }

    setContentMode("file");
    setMediaFile(file);
    setContentType(validation.contentType);
    setUploadError("");
    setUploadProgress(0);
    setMediaUrl("");
    setMuxPlaybackId("");
    setAutoThumbnail("");
    setArticleBody("");

    try {
      if (validation.contentType === "video") {
        setUploadPhase("uploading");
        const { uploadUrl, uploadId } = await createMuxDirectUpload();
        await uploadFileToMux(uploadUrl, file, setUploadProgress);
        setUploadPhase("processing");
        const muxResult = await waitForMuxReady(uploadId, { onProcessing: () => setUploadPhase("processing") });
        setMuxPlaybackId(muxResult.muxPlaybackId);
        setMediaUrl(`https://stream.mux.com/${muxResult.muxPlaybackId}.m3u8`);
        if (muxResult.thumbnailUrl) setAutoThumbnail(muxResult.thumbnailUrl);
        setUploadPhase("ready");
      } else {
        setUploadPhase("uploading");
        const folder = validation.contentType === "audio" ? "drops/audio" : "drops/photos";
        const result = await uploadMediaToStorage({
          file,
          creatorId: currentUser.uid,
          folder,
          onProgress: setUploadProgress,
        });
        setMediaUrl(result.url);
        if (validation.contentType === "photo") setAutoThumbnail(result.url);
        setUploadPhase("ready");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadPhase("error");
      setUploadError(err.message || "Upload failed.");
      showToast(err.message || "Upload failed.", "error");
    }
  }, [currentUser?.uid, showToast]);

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) processMediaFile(file);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processMediaFile(file);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const uploadThumbnail = async () => {
    if (thumbnailFile) {
      const path = `drops/thumbnails/${currentUser.uid}/${Date.now()}-${thumbnailFile.name.replace(/\s+/g, "-")}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, thumbnailFile);
      return getDownloadURL(fileRef);
    }
    return thumbnailPreview || autoThumbnail || "";
  };

  const buildDropPayload = async (status, { isUpdate = false } = {}) => {
    const thumbUrl = await uploadThumbnail();
    const numericPrice = isFree ? 0 : Math.max(1, parseFloat(price) || 1);
    const numericArchive = afterClose === "paid_archive" ? Math.max(1, parseFloat(archivePrice) || 1) : null;
    const resolvedContentType = contentMode === "article" ? "article" : contentType;
    const resolvedMediaUrl = contentMode === "article" ? articleBody.trim() : mediaUrl || null;

    const payload = {
      creatorId: currentUser.uid,
      title: title.trim(),
      subtitle: subtitle.trim(),
      description: description.trim(),
      thumbnailUrl: thumbUrl,
      contentType: resolvedContentType,
      muxPlaybackId: muxPlaybackId || null,
      mediaUrl: resolvedMediaUrl,
      price: numericPrice,
      isFree,
      tierAccess,
      afterClose,
      archivePrice: numericArchive,
      status,
      updatedAt: serverTimestamp(),
    };

    if (!isUpdate) {
      payload.viewCount = 0;
      payload.createdAt = serverTimestamp();
    }

    return payload;
  };

  const validateForm = (forPublish = true) => {
    if (forPublish && !contentReady) {
      showToast(
        contentMode === "article"
          ? `Write at least ${ARTICLE_MIN} characters before posting.`
          : "Upload and wait for processing to finish before posting.",
        "error"
      );
      return false;
    }
    if (!title.trim()) {
      showToast("Title is required.", "error");
      return false;
    }
    if (title.length > TITLE_MAX) {
      showToast(`Title must be ${TITLE_MAX} characters or less.`, "error");
      return false;
    }
    if (description.length > DESC_MAX) {
      showToast(`Description must be ${DESC_MAX} characters or less.`, "error");
      return false;
    }
    if (!isFree && (parseFloat(price) || 0) < 1) {
      showToast("Minimum price is $1.00 CAD.", "error");
      return false;
    }
    if (afterClose === "paid_archive" && (parseFloat(archivePrice) || 0) < 1) {
      showToast("Archive price minimum is $1.00 CAD.", "error");
      return false;
    }
    return true;
  };

  const handleSave = async (status) => {
    const forPublish = status === "open";
    if (!validateForm(forPublish)) return;
    setSubmitting(true);
    try {
      if (isEditMode) {
        const payload = await buildDropPayload(status, { isUpdate: true });
        await updateDoc(doc(db, "drops", editingDropId), payload);
        showToast(status === "open" ? "Drop updated." : "Draft updated.");
        setTimeout(() => navigate(`/drop/${editingDropId}`), 900);
      } else {
        const payload = await buildDropPayload(status);
        await addDoc(collection(db, "drops"), payload);
        if (status === "open") {
          showToast("Your drop is live — the window is open.");
        } else {
          showToast("Draft saved.");
        }
        setTimeout(() => navigate(MY_DROPS_PATH), 900);
      }
    } catch (err) {
      console.error("Save drop error:", err);
      showToast("Could not save drop. Try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const stepsDone = useMemo(() => [
    contentReady,
    title.trim().length > 0,
    Boolean(tierAccess),
    true,
    Boolean(afterClose),
  ], [contentReady, title, tierAccess, afterClose]);

  const activeStepIndex = useMemo(() => {
    const idx = stepsDone.findIndex((d) => !d);
    return idx === -1 ? 4 : idx;
  }, [stepsDone]);

  const previewThumb = thumbnailPreview || autoThumbnail;
  const previewTitle = title.trim() || "Your drop title";
  const isTierExclusive = tierAccess !== "everyone";
  const summaryEarnings = isFree ? "Free drop — no revenue" : receiveCalc.label;
  const summaryContentType = contentMode === "article"
    ? "Article"
    : contentType
      ? contentTypeSummary(contentType, mediaFile?.name)
      : "—";

  if (authLoading) {
    return (
      <CreatorStudioLayout activePath="/post">
        <div className="post-a-drop-page"><div className="page">Loading…</div></div>
      </CreatorStudioLayout>
    );
  }

  if (!currentUser) {
    return <Navigate to="/welcome?auth=signin" replace />;
  }

  if (!hasCreatorAccess(userProfile)) {
    return <Navigate to="/" replace />;
  }

  if (editLoading) {
    return (
      <CreatorStudioLayout activePath="/post">
        <div className="post-a-drop-page"><div className="page">Loading drop for editing…</div></div>
      </CreatorStudioLayout>
    );
  }

  if (editDropId && editLoadError) {
    return (
      <CreatorStudioLayout activePath="/post">
        <div className="post-a-drop-page">
          <div className="page">
            <h1 className="page-title">Couldn&apos;t open edit</h1>
            <p className="page-sub">{editLoadError}</p>
            <Link to="/post" className="btn-post">Start a new drop</Link>
          </div>
        </div>
      </CreatorStudioLayout>
    );
  }

  return (
    <CreatorStudioLayout activePath="/post">
      <div className="post-a-drop-page">
      <div className="page">
        <StepIndicator stepsDone={stepsDone} activeIndex={activeStepIndex} />

        <div className="page-eyebrow">Creator Studio</div>
        <h1 className="page-title">
          {isEditMode ? <>Edit <em>this drop</em></> : <>Post a <em>new drop</em></>}
        </h1>
        <p className="page-sub">
          {isEditMode
            ? "Update the details below. Changes save to the same drop."
            : "Once you post the window opens immediately. Make it count."}
        </p>

        <div className="info-banner">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C4790A" strokeWidth={STROKE} strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <p>
              <strong>Drops close permanently at exactly 100 views.</strong> No time limit — your drop stays open until 100 people view it. It never closes on time, only on views. All drops are equal opportunity — no subscriber gets a better position in the 100 view race.
            </p>
            <span className="info-hand">the window opens the moment you post</span>
          </div>
        </div>

        {/* Section 1 — Upload */}
        <section className="section">
          <div className="section-header">
            <div className="section-num">1</div>
            <div>
              <div className="section-title">Upload your content</div>
              <div className="section-sub">Video, photo, audio, or written content</div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/*,image/jpeg,image/png,image/*,audio/mpeg,audio/wav,audio/*"
            hidden
            onChange={handleFileInput}
          />

          {contentMode === "article" ? (
            <>
              <div className="article-editor-wrap">
                <div className="article-editor-toolbar">
                  <span>Written drop</span>
                  <span>{articleBody.length} characters</span>
                </div>
                <textarea
                  className="article-editor"
                  value={articleBody}
                  onChange={(e) => setArticleBody(e.target.value)}
                  placeholder="Write your article here…"
                />
              </div>
              <button type="button" className="article-toggle" onClick={switchToFile}>
                Upload a file instead
              </button>
            </>
          ) : uploadPhase === "idle" || uploadPhase === "error" ? (
            <>
              <div
                className={`upload-zone${dragOver ? " drag-over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
              >
                <div className="upload-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9A9088" strokeWidth={STROKE} strokeLinecap="round">
                    <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                </div>
                <h3>Drop your file here</h3>
                <p>
                  Video: MP4, MOV up to 8GB · 60 minutes max<br />
                  Photo: JPG, PNG up to 50MB · up to 12 photos<br />
                  Audio: MP3, WAV up to 500MB · Article: written in editor
                </p>
                <span className="upload-btn">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Choose file
                </span>
                {uploadError && <p style={{ color: "var(--red)", fontSize: 12, marginTop: 8 }}>{uploadError}</p>}
              </div>
              <button type="button" className="article-toggle" onClick={switchToArticle}>
                Write an article instead
              </button>
            </>
          ) : (
            <div className="upload-progress">
              <div className="upload-file-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C4790A" strokeWidth={STROKE} strokeLinecap="round">
                  <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
              </div>
              <div className="upload-file-info">
                <div className="upload-file-name">{mediaFile?.name}</div>
                <div className="upload-file-size">
                  {formatFileSize(mediaFile?.size)}
                  {contentType === "video" ? " · Uploading via Mux — resumable" : " · Uploading"}
                </div>
                {(uploadPhase === "uploading" || uploadPhase === "processing") && (
                  <div className="upload-prog-bar">
                    <div className="upload-prog-fill" style={{ width: `${uploadPhase === "processing" ? 100 : uploadProgress}%` }} />
                  </div>
                )}
                {uploadPhase === "processing" && <div className="upload-processing">Processing…</div>}
                {uploadPhase === "ready" && (
                  <div className="upload-processing" style={{ color: "var(--green)" }}>Ready to post</div>
                )}
              </div>
              <div className="upload-pct">
                {uploadPhase === "processing" ? "…" : uploadPhase === "ready" ? "✓" : `${uploadProgress}%`}
              </div>
            </div>
          )}
        </section>

        {/* Section 2 — Details */}
        <section className="section">
          <div className="section-header">
            <div className="section-num">2</div>
            <div>
              <div className="section-title">Drop details</div>
              <div className="section-sub">Name and describe your drop</div>
            </div>
          </div>

          <div className="field">
            <label>Title <span className="required">*</span></label>
            <input type="text" value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} placeholder="Morning Ritual Vol. 4" />
            <div className="char-count">{title.length} / {TITLE_MAX}</div>
          </div>
          <div className="field">
            <label>Subtitle <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(optional)</span></label>
            <input type="text" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="A short teaser line…" />
          </div>
          <div className="field">
            <label>Description <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(optional)</span></label>
            <textarea rows={3} value={description} maxLength={DESC_MAX} onChange={(e) => setDescription(e.target.value)} placeholder="Tell your fans what this drop is about…" />
            <div className="char-count">{description.length} / {DESC_MAX}</div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>
              Thumbnail{" "}
              <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>
                (optional — we&apos;ll use a frame from your video if not set)
              </span>
            </label>
            <input ref={thumbInputRef} type="file" accept="image/jpeg,image/png,image/*" hidden onChange={handleThumbnailChange} />
            <div className="thumb-row">
              <div className="thumb-preview">
                {previewThumb ? (
                  <img src={previewThumb} alt="" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9A9088" strokeWidth={STROKE} strokeLinecap="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                  </svg>
                )}
              </div>
              <button type="button" className="upload-btn" onClick={() => thumbInputRef.current?.click()}>
                Upload thumbnail
              </button>
            </div>
          </div>
        </section>

        {/* Section 3 — Audience */}
        <section className="section">
          <div className="section-header">
            <div className="section-num">3</div>
            <div>
              <div className="section-title">Who can see this drop?</div>
              <div className="section-sub">Regular drops are equal for everyone — no subscriber gets priority over the 100 spots</div>
            </div>
          </div>

          <OptionCard
            wide
            selected={tierAccess === "everyone"}
            onClick={() => setTierAccess("everyone")}
            title="Everyone — regular drop"
            desc="Appears in Gallery and Home feed. All 100 spots open equally to subscribers and non-subscribers. First come first served — no exceptions."
            icon={(
              <div className="option-icon" style={{ background: "var(--gold-light)", marginBottom: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C4790A" strokeWidth={STROKE} strokeLinecap="round">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                </svg>
              </div>
            )}
          />

          <div className="tier-panel">
            <div className="tier-panel-label">Or make it exclusive to a tier:</div>
            <div className="tier-grid">
              {hasTierSetup ? (
                EXCLUSIVE_TIER_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`tier-card${tierAccess === opt.id ? " selected" : ""}`}
                    onClick={() => setTierAccess(opt.id)}
                  >
                    <h4>{opt.label}</h4>
                    <p>{opt.desc}</p>
                  </button>
                ))
              ) : (
                <>
                  {EXCLUSIVE_TIER_OPTIONS.slice(0, 5).map((opt) => (
                    <div key={opt.id} className="tier-card empty" style={{ opacity: 0.45 }}>
                      <h4>{opt.label}</h4>
                      <p>{opt.desc}</p>
                    </div>
                  ))}
                  <Link to="/support-tiers" className="tier-card empty tier-card-link">
                    <h4>No tiers set up</h4>
                    <p>Set up tiers first →</p>
                  </Link>
                </>
              )}
            </div>
            <div className="caveat">Tier-exclusive drops don&apos;t appear in public Gallery — only visible to eligible subscribers</div>
          </div>
        </section>

        {/* Section 4 — Pricing */}
        <section className="section">
          <div className="section-header">
            <div className="section-num">4</div>
            <div>
              <div className="section-title">Access &amp; pricing</div>
              <div className="section-sub">How much do fans pay to view this drop during the window?</div>
            </div>
          </div>

          <div className="option-grid-2" style={{ marginBottom: 20 }}>
            <OptionCard
              selected={isFree}
              variant="green"
              onClick={() => setIsFree(true)}
              title="Free"
              desc="Anyone who gets to a spot sees it free. Great for growing your audience and pulling new followers in."
              icon={(
                <div className="option-icon" style={{ background: "var(--green-light)" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1A8C52" strokeWidth={STROKE} strokeLinecap="round">
                    <path d="M18 8h1a4 4 0 010 8h-1" /><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" />
                    <line x1="6" y1="1" x2="6" y2="4" /><line x1="10" y1="1" x2="10" y2="4" /><line x1="14" y1="1" x2="14" y2="4" />
                  </svg>
                </div>
              )}
            />
            <OptionCard
              selected={!isFree}
              onClick={() => setIsFree(false)}
              title="Paid unlock"
              desc={`Fans pay a one-time price to claim their spot. You set the price — we take 10%${founding ? " (founding rate, locked forever)" : ""}.`}
              icon={(
                <div className="option-icon" style={{ background: "var(--gold-light)" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C4790A" strokeWidth={STROKE} strokeLinecap="round">
                    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                  </svg>
                </div>
              )}
            />
          </div>

          <div className={`field-row${isFree ? " is-disabled" : ""}`}>
            <div className="field">
              <label>Price (CAD) {!isFree && <span className="required">*</span>}</label>
              <div className="price-input-wrap">
                <span className="price-symbol">$</span>
                <input type="number" value={price} step="0.01" min="1" disabled={isFree} onChange={(e) => setPrice(e.target.value)} />
              </div>
              <div className="field-hint">Shown when Paid is selected · Minimum $1.00</div>
              {!isFree && (
                <div className="field-hint field-hint--muted" style={{ marginTop: 8 }}>
                  Drops priced between $5–$10 typically see the highest unlock rates. You&apos;re always in control of your pricing.
                </div>
              )}
            </div>
            <div className="field">
              <label>You&apos;ll receive per unlock</label>
              <input type="text" value={isFree ? "—" : receiveCalc.label} disabled readOnly style={{ background: "var(--bg)", color: "var(--text-muted)", cursor: "not-allowed" }} />
              {founding && !isFree && (
                <div className="field-hint" style={{ color: "var(--green)" }}>Founding creator rate · Locked forever</div>
              )}
            </div>
          </div>
        </section>

        {/* Section 5 — After close */}
        <section className="section">
          <div className="section-header">
            <div className="section-num">5</div>
            <div>
              <div className="section-title">What happens after 100 views?</div>
              <div className="section-sub">The window closes at 100 — what comes next is your choice</div>
            </div>
          </div>

          <div className="option-grid-2">
            <OptionCard
              selected={afterClose === "gone_forever"}
              variant="dark"
              onClick={() => setAfterClose("gone_forever")}
              title="Gone forever"
              desc="Drop closes permanently at 100 views. Nobody else ever sees it — no exceptions, no payment option. The first 100 have something the world never will."
              hand="maximum exclusivity"
              icon={(
                <div className="option-icon" style={{ background: "#F0EDE8" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0E0C0A" strokeWidth={STROKE} strokeLinecap="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </div>
              )}
            />
            <OptionCard
              selected={afterClose === "paid_archive"}
              onClick={() => setAfterClose("paid_archive")}
              title="Paid archive"
              desc="After 100 views the drop locks and becomes a permanent paid purchase. Anyone can buy it at any time. No rank badge for archive buyers — they missed the window."
              hand="best for evergreen content"
              icon={(
                <div className="option-icon" style={{ background: "var(--gold-light)" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C4790A" strokeWidth={STROKE} strokeLinecap="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              )}
            />
          </div>

          <div className={`archive-panel${afterClose !== "paid_archive" ? " is-disabled" : ""}`}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Archive price (CAD)</label>
              <div className="price-input-wrap">
                <span className="price-symbol">$</span>
                <input
                  type="number"
                  value={archivePrice}
                  step="0.01"
                  min="1"
                  placeholder="9.00"
                  disabled={afterClose !== "paid_archive"}
                  onChange={(e) => setArchivePrice(e.target.value)}
                />
              </div>
              <div className="field-hint">The price fans pay to access this drop after it closes. Can be different from the window price.</div>
              <div className="field-hint field-hint--muted" style={{ marginTop: 8 }}>
                We recommend pricing archive access slightly lower than your original drop — it rewards fans who act fast.
              </div>
            </div>
          </div>
        </section>

        {/* Summary */}
        <div className="summary-box">
          <div className="summary-title">Drop summary</div>
          <div className="summary-row">
            <span className="summary-label">Title</span>
            <span className="summary-value">{title.trim() || "—"}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Audience</span>
            <span className={`summary-value${tierAccess === "everyone" ? " gold" : ""}`}>{tierAccessLabel(tierAccess)}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Access during window</span>
            <span className={`summary-value${isFree ? " green" : ""}`}>
              {isFree ? "Free" : `$${parseFloat(price || 0).toFixed(2)} CAD`}
            </span>
          </div>
          <div className="summary-row">
            <span className="summary-label">After 100 views</span>
            <span className="summary-value">{afterCloseLabel(afterClose)}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Content type</span>
            <span className="summary-value">{summaryContentType}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Your earnings</span>
            <span className="summary-value gold">{summaryEarnings}</span>
          </div>
        </div>

        {/* Preview */}
        <div className="preview-section-label">How it&apos;ll look in Gallery</div>
        <div className="preview-card">
          <div className="preview-thumb">
            {previewThumb ? (
              <img src={previewThumb} alt="" />
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round">
                <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            )}
            <div className={`preview-price-badge${isFree ? " free" : ""}`}>
              {isFree ? "Free" : `$${parseFloat(price || 0).toFixed(2)}`}
            </div>
            {isTierExclusive && <div className="preview-tier-badge">Tier exclusive</div>}
            <div className="preview-views">0 / 100</div>
          </div>
          <div className="preview-body">
            <div className="preview-creator">
              <img className="preview-avatar" src={userProfile?.avatar || userProfile?.avatarUrl || "https://i.pravatar.cc/80"} alt="" />
              <span className="preview-creator-name">{userProfile?.displayName || "You"}</span>
            </div>
            <div className="preview-title">{previewTitle}</div>
            <div className="preview-prog"><div className="preview-prog-fill" /></div>
            <div className="preview-after">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round">
                <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
              {afterClose === "paid_archive" ? "Paid archive after 100 views" : "Gone forever after 100 views"}
            </div>
          </div>
        </div>

        <div className="action-row">
          {!isEditMode && (
            <button type="button" className="btn-cancel" disabled={submitting} onClick={() => handleSave("draft")}>
              Save as draft
            </button>
          )}
          {isEditMode && (
            <button type="button" className="btn-cancel" disabled={submitting} onClick={() => navigate(`/drop/${editingDropId}`)}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="btn-post-big"
            disabled={submitting}
            onClick={() => handleSave(isEditMode ? originalStatus : "open")}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
            {submitting
              ? (isEditMode ? "Saving…" : "Posting…")
              : (isEditMode ? "Save changes" : "Post drop — open the window")}
          </button>
        </div>
      </div>

      {toast && (
        <div className={`toast${toast.type === "error" ? " error" : ""}`}>{toast.text}</div>
      )}
      </div>
    </CreatorStudioLayout>
  );
}
